/**
 * Wayfinding search (A18) — deterministic menu lookup. Pure, generic,
 * shared so both the LIFF palette and a test can use it. No LLM, no tenant
 * data, role-scoped: it tells you where a page is, never does anything.
 */
export interface MenuEntry {
  label: string;
  route: string;
  roles: string[]; // roles allowed to see this destination
  keywords?: string[]; // extra search terms (synonyms, EN)
}

export interface MenuMatch extends MenuEntry {
  score: number;
}

/** Normalize for matching: lowercase + strip spaces. */
function norm(s: string): string {
  return s.toLowerCase().replace(/\s+/g, '');
}

/**
 * Rank menu entries against a free-text query, scoped to `role`.
 *  3 = exact label · 2 = label prefix · 1 = label/keyword substring.
 * Entries the role can't access are never returned.
 */
export function searchMenu(entries: MenuEntry[], query: string, role: string): MenuMatch[] {
  const q = norm(query);
  if (!q) return [];
  const out: MenuMatch[] = [];
  for (const e of entries) {
    if (!e.roles.includes(role)) continue; // role-scoped — no leaking other roles' pages
    const label = norm(e.label);
    let score = 0;
    if (label === q) score = 3;
    else if (label.startsWith(q)) score = 2;
    else if (label.includes(q)) score = 1;
    else if ((e.keywords ?? []).some((k) => norm(k).includes(q))) score = 1;
    if (score > 0) out.push({ ...e, score });
  }
  return out.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
}
